import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getFeatureFlag } from 'ctm-selectors/selectors/base';
import { MuiThemeProvider, createTheme } from '@material-ui/core';
import { resultsConfigSelector } from 'ctm-selectors';
import Dialog from '@material-ui/core/Dialog';
import { withRules, SimpleFlexbox, Text, ModalCloseIcon } from 'ctm-components';
import { isDstObserved } from 'ctm-util-functions';
import { getCallCentreOpenHours } from '../../../core/state_selectors';

const theme = createTheme({
    overrides: {
        MuiPaper: {
            root: {
                padding: '2rem',
                // To fix the absolute-positioned close icon being cut off
                overflowY: 'unset !important'
            }
        }
    }
});

/**
 * This is the Call Centre Hours Modal - with opening hours based timezone.
 * The call centre hours are from a json file including opening times in both AEST and AEDT.
 * @param {Object} props React props.
 * @returns {CallCentreHoursModal} The Call Centre Hours modal
 */
const _CallCentreHoursModal =  (props) => {
    const showCallCentreHealthHours = getFeatureFlag('showCallCentreHealthHours');
    const { callCentreHoursModalIsOpen, openHours, closeModal, centres } = props;

    const OpeningHours = ({ centre, children }) => {
        const centreHours = openHours[centre.toLowerCase()];
        if (!centreHours) return null;
        return (
            <SimpleFlexbox>
                <Text style={{ textTransform: 'capitalize' }} tagName="h3" color="brand_primary">{children}</Text>
                {centreHours.times.map((time) => (
                    <SimpleFlexbox display="flex" key={`${centre}-${time.name}`}>
                        <SimpleFlexbox flex={1}>
                            <span>{time.name}</span>
                        </SimpleFlexbox>
                        <SimpleFlexbox flex={1}>
                            <span>{`${time.startTime} - ${time.endTime}`}</span>
                        </SimpleFlexbox>
                    </SimpleFlexbox>
                ))}
                <Text tagName="strong" innerHTML={centre === 'Health' ? 'The above opening hours apply Australia-wide.' : `Call centre hours are based on ${centreHours.timeZone}.`} />
            </SimpleFlexbox>
        );
    };

    OpeningHours.propTypes = {
        centre: PropTypes.string,
        children: PropTypes.node
    };

    /**
   * Render top-right modal close icon button
   * @return {Node} the CloseIcon button
   */
    const renderCloseIcon = () => {
        const closeIconProps = { onClickFunction: closeModal };
        return React.createElement(ModalCloseIcon, closeIconProps);
    };

    const displayText = ({ centre }) => {
        if (centre === 'Car') {
            return  'Car/Home & Contents';
        }
        return centre;
    };

    const centresForBrand = () => {
        if (!centres) {
            return centres;
        }
        let filteredCentres = [...centres];
        if (!showCallCentreHealthHours) {
            filteredCentres = filteredCentres.filter((center) => center !== 'Health');
        }
        return filteredCentres;
    };
    return (
        <MuiThemeProvider theme={theme}>
            <Dialog fullWidth open={callCentreHoursModalIsOpen} onClose={closeModal} disableBackdropClick disableEscapeKeyDown>
                <SimpleFlexbox display="flex" column>
                    {centresForBrand().map((centre) => (
                        <OpeningHours centre={centre}>
                            {displayText({ centre })}
                            {' '}
                            Call Centre - Opening hours
                        </OpeningHours>
                    ))}
                </SimpleFlexbox>
                {renderCloseIcon()}
            </Dialog>
        </MuiThemeProvider>
    );
};

_CallCentreHoursModal.propTypes = {
    openHours: PropTypes.shape({
        times: PropTypes.arrayOf(
            PropTypes.shape({
                starTime: PropTypes.string,
                endTime: PropTypes.string,
                name: PropTypes.string
            })
        ),
        timeZone: PropTypes.string
    }),
    callCentreHoursModalIsOpen: PropTypes.bool,
    closeModal: PropTypes.func,
    centres: PropTypes.arrayOf(PropTypes.string)
};

/**
 * mapStateToProps - sets up the rules required by this component
 * @param {Object} state - Redux store state.
 * @return {Object} - The state attributes to add to props.
 */
const mapStateToProps = (state) => {
    const openHours = getCallCentreOpenHours({ state }, isDstObserved());
    const callCentreHoursModal = resultsConfigSelector(state).getIn(['helpers', 'callCentreHours']);

    return {
        callCentreHoursModalIsOpen: callCentreHoursModal ? callCentreHoursModal.open : false,
        openHours
    };
};

const CallCentreHoursModal = withRules(connect(mapStateToProps)(_CallCentreHoursModal));

export { _CallCentreHoursModal, CallCentreHoursModal };
